import { Layout } from "@jambushrushti/components/layout/Layout";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  BookOpen,
  Scroll,
  Image as ImageIcon,
  Video,
  MapPin,
  Clock,
  Mail,
  ExternalLink,
  Quote,
  Globe,
  MessageSquare,
  PlayCircle,
  Users,
  FileText,
  GraduationCap,
  Languages,
  PenLine,
  BookMarked,
  Library,
} from "lucide-react";

const facilities = [
  {
    icon: Scroll,
    title: "Manuscript Archives",
    description:
      "Rare handwritten manuscripts, palm leaf texts, and historical documents preserved with utmost care",
  },
  {
    icon: BookOpen,
    title: "Library Section",
    description:
      "Extensive collection of published works, including Gurudev's edited Agamas and philosophical texts",
  },
  {
    icon: ImageIcon,
    title: "Photo Gallery",
    description:
      "Historical photographs documenting Gurudev's life, travels, and scholarly activities",
  },
  {
    icon: Video,
    title: "Audio-Visual Archives",
    description:
      "Recordings of lectures, discourses, and documentaries about Gurudev's contributions",
  },
];

const highlights = [
  {
    title: "Original Manuscripts",
    count: "500+",
    description: "Rare handwritten texts",
  },
  {
    title: "Published Works",
    count: "200+",
    description: "Books and publications",
  },
  {
    title: "Historical Photos",
    count: "1000+",
    description: "Archival photographs",
  },
  {
    title: "Audio Recordings",
    count: "100+",
    description: "Lectures and discourses",
  },
];

const testimonials = [
  {
    name: "Dr. Ramesh Jain",
    designation: "Professor, Jain Studies",
    institution: "University of Mumbai",
    quote:
      "Muni Jambuvijayaji Research Center is an invaluable resource for Jain scholarship. The manuscript collection is unparalleled, and the staff's dedication to preservation is commendable.",
    image:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
  },
  {
    name: "Prof. Nalini Shah",
    designation: "Research Scholar",
    institution: "Gujarat University",
    quote:
      "My research on Prakrit literature was transformed by the resources available here. The digital archives and expert guidance made my work significantly easier.",
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
  },
  {
    name: "Dr. Mahavir Sanghi",
    designation: "Indologist",
    institution: "Bhandarkar Oriental Research Institute",
    quote:
      "This center bridges traditional Jain knowledge with modern research methodologies. A must-visit for anyone serious about Agamic studies.",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
  },
];

const indologyFeatures = [
  {
    icon: Scroll,
    title: "Manuscript Studies",
    description:
      "Explore ancient Jain manuscripts with scholarly annotations and detailed textual analysis",
  },
  {
    icon: PenLine,
    title: "Multi-Script Support",
    description:
      "Access texts in Prakrit, Sanskrit, Gujarati, and Devanagari with transliteration support",
  },
  {
    icon: BookMarked,
    title: "Comparative Studies",
    description:
      "Research connections between Jain literature and Vedic, Buddhist, and Sanskrit traditions",
  },
  {
    icon: Library,
    title: "Indology Resources",
    description:
      "Comprehensive databases connecting Jain philosophy with broader Indian scholarly traditions",
  },
];

const forumFeatures = [
  {
    icon: MessageSquare,
    title: "Discussion Forums",
    description:
      "Engage with scholars worldwide on topics ranging from Agamic interpretation to manuscript authentication",
    link: "/scholar/feed",
    linkText: "Join Discussions",
  },
  {
    icon: PlayCircle,
    title: "Lecture Repository",
    description:
      "Access recorded lectures from renowned scholars on Jain philosophy, history, and textual studies",
    link: "/learning/courses",
    linkText: "Browse Lectures",
  },
  {
    icon: Users,
    title: "Scholar Network",
    description:
      "Connect with researchers, professors, and students from institutions worldwide",
    link: "/scholars",
    linkText: "View Directory",
  },
  {
    icon: FileText,
    title: "Indology Research",
    description:
      "Explore manuscript analysis tools, cross-reference databases, and collaboration opportunities",
    link: "/research/indology",
    linkText: "Explore Indology",
  },
];

const GyanKendra = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="animate-fade-up mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-secondary/80 px-4 py-2 text-sm">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Digital Museum</span>
            </div>
            <h1 className="font-display mb-6 text-4xl font-bold text-foreground sm:text-5xl">
              Muni Jambuvijayaji Research Center
            </h1>
            <p className="text-lg text-muted-foreground">
              A dedicated center preserving Gurudev Muni Jambuvijayaji Maharaj
              Saheb's scholarly works, manuscripts, and teachings for future
              generations.
            </p>
          </div>
        </div>
      </section>

      {/* About the Kendra */}
      <section className="bg-background py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
            <div className="animate-fade-up">
              <h2 className="font-display mb-6 text-3xl font-bold text-foreground">
                Preserving <span className="text-primary">Wisdom</span> for
                Generations
              </h2>
              <p className="mb-4 text-muted-foreground">
                Muni Jambuvijayaji Research Center stands as a testament to
                Gurudev's extraordinary life of scholarship and spiritual
                service. Located in Shantigram, this center houses an extensive
                collection of manuscripts, books, photographs, and audio-visual
                materials.
              </p>
              <p className="mb-6 text-muted-foreground">
                The Kendra serves as both a museum and a research facility,
                welcoming scholars, students, and devotees who wish to explore
                and study Gurudev's contributions to Jain literature and
                philosophy.
              </p>

              {/* Stats */}
              <div className="mb-6 grid grid-cols-2 gap-4">
                {highlights.map((item, index) => (
                  <div
                    key={item.title}
                    className="rounded-xl border border-border bg-secondary/50 p-4"
                  >
                    <div className="font-display mb-1 text-2xl font-bold text-primary">
                      {item.count}
                    </div>
                    <div className="text-sm font-medium text-foreground">
                      {item.title}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {item.description}
                    </div>
                  </div>
                ))}
              </div>

              <Button variant="hero" asChild>
                <Link to="/gallery">
                  Explore Gallery
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="animate-fade-up relative delay-200">
              <div className="shadow-elevated aspect-[4/3] overflow-hidden rounded-2xl">
                <img
                  src="https://siddhijambuparivar.com/wp-content/uploads/2022/11/111-min.jpg"
                  alt="Research Center"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="shadow-soft absolute -bottom-4 -left-4 rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-foreground">
                      Research Center
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Shantigram, Gujarat
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="bg-gradient-spiritual py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="font-display mb-4 text-3xl font-bold text-foreground">
              What You'll Find Here
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Explore the various sections and facilities available at Muni
              Jambuvijayaji Research Center
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {facilities.map((item, index) => (
              <Card
                key={item.title}
                variant="feature"
                className="animate-fade-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6 text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                    <item.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-display mb-2 font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Indology & Research Section */}
      <section className="bg-background py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12 text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-sm">
                <BookOpen className="h-4 w-4 text-primary" />
                <span className="font-medium text-primary">
                  Scholarly Research
                </span>
              </div>
              <h2 className="font-display mb-4 text-3xl font-bold text-foreground">
                Indology & Comparative Studies
              </h2>
              <p className="mx-auto max-w-2xl text-muted-foreground">
                Explore the rich traditions of Jain scholarship and their
                connections to broader Indian philosophical heritage through our
                comprehensive research resources.
              </p>
            </div>

            <div className="mb-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {indologyFeatures.map((item, index) => (
                <Card
                  key={item.title}
                  variant="feature"
                  className="animate-fade-up group transition-colors hover:border-primary/30"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-6">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-colors group-hover:bg-primary/20">
                      <item.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-display mb-2 font-semibold text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="rounded-2xl border border-border bg-secondary/30 p-8">
              <div className="grid items-center gap-8 md:grid-cols-2">
                <div>
                  <h3 className="font-display mb-4 text-xl font-bold text-foreground">
                    Specialized Research Areas
                  </h3>
                  <p className="mb-4 text-muted-foreground">
                    Our Indology division offers in-depth resources for
                    specialized scholarly research, connecting ancient Jain
                    wisdom with contemporary academic methodologies.
                  </p>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                      <span>
                        <strong className="text-foreground">
                          Agamic Studies
                        </strong>{" "}
                        - Critical analysis of Jain canonical texts,
                        Anga-pravishta and Anga-bahya literature
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                      <span>
                        <strong className="text-foreground">
                          Prakrit Linguistics
                        </strong>{" "}
                        - Study of Ardhamagadhi, Shauraseni, and other Prakrit
                        dialects in Jain scriptures
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                      <span>
                        <strong className="text-foreground">
                          Philosophical Commentaries
                        </strong>{" "}
                        - Research on Tika, Churni, and Bhashya traditions
                        across centuries
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                      <span>
                        <strong className="text-foreground">
                          Manuscript Paleography
                        </strong>{" "}
                        - Analysis of scripts, dating methods, and textual
                        variants
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                      <span>
                        <strong className="text-foreground">
                          Comparative Philosophy
                        </strong>{" "}
                        - Connections with Vedic, Buddhist, and Sanskrit
                        scholarly traditions
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="flex justify-center">
                  <Button variant="hero" asChild>
                    <Link to="/research">
                      Explore Research Tools
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Academic Discussion Forum & Lecture Repository */}
      <section className="bg-gradient-spiritual py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12 text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-sm">
                <GraduationCap className="h-4 w-4 text-primary" />
                <span className="font-medium text-primary">
                  Academic Community
                </span>
              </div>
              <h2 className="font-display mb-4 text-3xl font-bold text-foreground">
                Discussion Forum & Lecture Repository
              </h2>
              <p className="mx-auto max-w-2xl text-muted-foreground">
                Join a vibrant community of scholars and access an extensive
                library of academic lectures, discussions, and research
                materials.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {forumFeatures.map((item, index) => (
                <Card
                  key={item.title}
                  variant="feature"
                  className="animate-fade-up group"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 transition-colors group-hover:bg-primary/20">
                        <item.icon className="h-7 w-7 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display mb-2 font-semibold text-foreground">
                          {item.title}
                        </h3>
                        <p className="mb-4 text-sm text-muted-foreground">
                          {item.description}
                        </p>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={item.link}>
                            {item.linkText}
                            <ArrowRight className="ml-1 h-3 w-3" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-10 text-center">
              <p className="mb-4 text-muted-foreground">
                Ready to contribute to the scholarly community?
              </p>
              <Button variant="hero" asChild>
                <Link to="/scholar/login">
                  Join as Scholar
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-background py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12 text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-sm">
                <Quote className="h-4 w-4 text-primary" />
                <span className="font-medium text-primary">Testimonials</span>
              </div>
              <h2 className="font-display mb-4 text-3xl font-bold text-foreground">
                What Scholars Say
              </h2>
              <p className="mx-auto max-w-2xl text-muted-foreground">
                Hear from researchers and academics who have benefited from our
                resources
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {testimonials.map((item, index) => (
                <Card
                  key={item.name}
                  variant="feature"
                  className="animate-fade-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-6">
                    <Quote className="mb-4 h-8 w-8 text-primary/20" />
                    <p className="mb-6 text-sm italic leading-relaxed text-muted-foreground">
                      "{item.quote}"
                    </p>
                    <div className="flex items-center gap-3 border-t border-border pt-4">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-display text-sm font-semibold text-foreground">
                          {item.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {item.designation}
                        </div>
                        <div className="text-xs text-primary">
                          {item.institution}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Visit Information */}
      <section className="bg-gradient-spiritual py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <div className="mb-12 text-center">
              <h2 className="font-display mb-4 text-3xl font-bold text-foreground">
                Plan Your Visit
              </h2>
              <p className="text-muted-foreground">
                Experience the Research Center in person and explore Gurudev's
                legacy
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              <Card variant="feature" className="animate-fade-up">
                <CardContent className="p-6">
                  <h3 className="font-display mb-4 text-lg font-semibold text-foreground">
                    Visiting Hours
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Clock className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium text-foreground">
                          Mon - Sat
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Office: 10:00 AM - 6:00 PM IST
                          <br />
                          Visiting: 10:45 AM - 5:15 PM IST
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      * Closed on Sundays, major Jain festivals and special
                      occasions
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card variant="feature" className="animate-fade-up delay-100">
                <CardContent className="p-6">
                  <h3 className="font-display mb-4 text-lg font-semibold text-foreground">
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium text-foreground">
                          Location
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Shantigram, Gujarat, India
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-primary" />
                      <a
                        href="mailto:contact@jambushrusti.org"
                        className="text-sm text-primary hover:underline"
                      >
                        contact@jambushrusti.org
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* External Link */}
      <section className="bg-gradient-hero lotus-pattern py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display mb-4 text-2xl font-bold text-foreground">
            Visit Siddhi Jambu Parivar Website
          </h2>
          <p className="mx-auto mb-6 max-w-xl text-muted-foreground">
            For more information about events, updates, and resources from the
            Parivar
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="hero" asChild>
              <a
                href="https://siddhijambuparivar.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit Official Website
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/about/gurudev">Read Gurudev's Biography</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default GyanKendra;
